import React, { useState, useEffect, useRef, useMemo } from 'react';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { fetchSubscriptionUsers } from '../../api/user';
import { Box, Button, IconButton, Modal, Pagination } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate } from '../../utils/dateTime';
import { style2 } from '../user-management/UserList';
import ProfileCard from '../user-management/ProfileCard';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../common/Loader/Loader';
import UserTableSkeleton from '../user-management/UserTableSkeleton';
import Tooltip from '@mui/material/Tooltip';
import { setSearchTermAction } from '../../containers/app-container/reducer/filterSlice';
import { initColumns, toggleColumn } from '../../containers/app-container/reducer/visibleColumnsSlice';
import { statusLabel } from '../../utils/memberStatus';

// Account status: the server's `statusBucket` (/stat/subscription/users). The active/
// Deactivated split is only the rollout fallback for a server that does not send it yet.
const accountStatusLabel = (u) => {
  if (u.isDeletedUser) return 'Deleted';
  if (u.statusBucket) return statusLabel(u.statusBucket);
  return u.active ? 'Active' : 'Deactivated';
};
// Column order here drives BOTH the table header/cell order AND the
// Manage Columns dropdown order — change the order in this one list and
// the rendered table follows. Each column gets a `key`, `label` (used in
// the dropdown + header), and `render(user, ctx)` returning the cell body.
// `cellClass` is applied to the <td> only.
const buildColumnDefs = ({ handleOpenTransactions, handleOpenProfile }) => [
  {
    key: 'subscription',
    label: 'Subscription Status',
    // A refunded member (status 'Refunded') is shown here for reference but is NOT a
    // held subscriber and is counted in no metric/revenue — so it gets its own amber
    // tag, distinct from Active (green) and Cancelled/Expired (red).
    cellClass: (user) =>
      `text-center text-white ${
        user.subscriptionStatus === 'Active'
          ? 'bg-success-700'
          : user.subscriptionStatus === 'Refunded'
            ? 'bg-warning-600'
            : user.subscriptionStatus === 'Cancelled' || user.subscriptionStatus === 'Expired'
              ? 'bg-danger-500'
              : 'bg-warning-600'
      }`,
    render: (u) => u.subscriptionStatus,
  },
  {
    key: 'subscriberType',
    label: 'Subscriber Type',
    render: (u) => {
      if (!u.subscriberType) return '-';
      const cls =
        u.subscriberType === 'New'
          ? 'bg-success-100 text-success-600'
          : u.subscriberType === 'Renewal'
            ? 'bg-info-100 text-info-600'
            : 'bg-danger-100 text-danger-600';
      return (
        <span className={`badge text-sm fw-medium px-12 py-4 radius-4 ${cls}`}>
          {u.subscriberType}
        </span>
      );
    },
  },
  {
    key: 'username',
    label: 'Username',
    render: (u) => (
      <span className="d-inline-flex align-items-center gap-1">
        {u.firstName ?? u.userName}
        {u.isDeletedUser && (
          <Tooltip title="Account deleted — subscription still counted" arrow placement="top">
            <span className="badge text-xs fw-medium px-8 py-2 radius-4 bg-danger-100 text-danger-600">
              Deleted
            </span>
          </Tooltip>
        )}
      </span>
    ),
  },
  { key: 'email', label: 'Email', render: (u) => u.email },
  { key: 'age', label: 'Age', render: (u) => u.age },
  { key: 'gender', label: 'Gender', render: (u) => u.gender },
  {
    key: 'registrationDate',
    label: 'Registration Date',
    render: (u) => (u?.registrationDate ? formatDate(u.registrationDate) : ''),
  },
  {
    key: 'subscriptionDate',
    label: 'Subscription Date',
    render: (u) => (u?.startDate ? formatDate(u.startDate) : ''),
  },
  {
    key: 'nextRenewalDate',
    label: 'Next Renewal Date',
    render: (u) =>
      u?.subscriptionStatus === 'Cancelled'
        ? ''
        : u?.nextRenewalDate
          ? formatDate(u.nextRenewalDate)
          : '-',
  },
  {
    key: 'cancelledOn',
    label: 'Cancelled On',
    render: (u) => (u?.cancelledDate ? formatDate(u.cancelledDate) : '-'),
  },
  {
    key: 'totalTransactionCount',
    label: 'Total Transactions',
    render: (u) => (
      <div className="d-flex align-items-center justify-content-center gap-1">
        <span>{u?.totalTransactionCount ?? 0}</span>
        {(u?.totalTransactionCount ?? 0) > 0 && (
          <Tooltip title="View transactions" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => handleOpenTransactions(u)}
              sx={{ p: 0.25 }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    ),
  },
  {
    key: 'totalTransactionAmount',
    label: 'Total Amount',
    render: (u) =>
      (u?.totalTransactionAmount ?? 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    key: 'transactionCurrency',
    label: 'Currency',
    render: (u) =>
      u?.transactionCurrency ? String(u.transactionCurrency).toUpperCase() : '-',
  },
  {
    key: 'planStatus',
    label: 'Plan Status',
    render: (u) => (
      <div className="DimondGold_profile">
        {u.subscriptionType === 'Luxury' && (
          <Button className="Dimond">
            <img src="/assets/images/user/user-profile/luxury-icon.svg" alt="diamond icon" /> Luxury
          </Button>
        )}
        {u.subscriptionType === 'Gold' && (
          <Button className="Gold">
            <img src="/assets/images/user/user-profile/gold-icon.svg" alt="gold icon" /> Gold
          </Button>
        )}
      </div>
    ),
  },
  {
    key: 'location',
    label: 'Location',
    render: (u) => [u.city, u.state, u.country].filter(Boolean).join(', '),
  },
  {
    key: 'accountStatus',
    label: 'Account Status',
    render: (u) => accountStatusLabel(u),
  },
  {
    key: 'action',
    label: 'Action',
    render: (u) => (
      <div className="dropdown">
        <button
          className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon text-sm"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          Action
        </button>
        <ul className="dropdown-menu">
          <li>
            <a
              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
              href="javascript:void(0)"
              onClick={() => handleOpenProfile(u)}
            >
              View
            </a>
          </li>
        </ul>
      </div>
    ),
  },
];

// `lifetimeOnLoad` — open on the FULL history and start honouring the date picker only once
// the user actually moves it. The picker always holds a range (last 30 days by default), so
// without this the Subscription Report answered "who subscribed in the last 30 days" when the
// question it exists for is "who has ever subscribed" — which hid 15 of July's 16 subscribers
// and 13 of August's 15. Off for the Financials screen, where the list sits beside revenue
// cards that ARE date-scoped and a lifetime list next to a 30-day card would just mislead.
const SubscribedUserList = ({ hideRevenue = false, lifetimeOnLoad = false }) => {
  const searchTermFromStore = useSelector((state) => state.filter.searchTerm);
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState(null);
  const visibleColumns = useSelector(state => state.visibleColumns.pages.financials || {});

  const tableContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  const handleColumnToggle = (key) => {
    dispatch(
      toggleColumn({
        pageKey: "financials",
        column: key
      }))
  };

  const [userData, setUserData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(false);
  const [openProfile, setOpenProfile] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLimit, setPageLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [transactionsModalRow, setTransactionsModalRow] = useState(null);

  const handleOpenTransactions = (row) => setTransactionsModalRow(row);
  const handleCloseTransactions = () => setTransactionsModalRow(null);
  // Scroll thumb width calculation
  const thumbWidth = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.clientWidth / scrollState.scrollWidth;
      return Math.max(ratio * scrollState.clientWidth, 50);
    }
    return 100;
  }, [scrollState]);

  // Scroll thumb position
  const thumbPosition = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
      return ratio * (scrollState.clientWidth - thumbWidth);
    }
    return 0;
  }, [scrollState, thumbWidth]);

  const updateScrollState = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setScrollState({
        scrollLeft,
        scrollWidth,
        clientWidth,
      });
    }
  };

  useEffect(() => {
    dispatch(
      initColumns({
        pageKey: "financials",
        columns: columnDefs.map((c) => c.key)
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableScroll = () => {
    updateScrollState();
  };

  const handleStickyScroll = (e) => {
    if (!tableContainerRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const thumbCenter = thumbWidth / 2;

    const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) *
      (scrollState.scrollWidth - scrollState.clientWidth);

    tableContainerRef.current.scrollLeft = newScrollLeft;
  };

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startScrollLeft = scrollState.scrollLeft;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const trackWidth = scrollState.clientWidth - thumbWidth;
      const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;

      if (trackWidth > 0) {
        const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => {
      window.removeEventListener('resize', updateScrollState);
    };
  }, [userData, visibleColumns]);
  // const { filter } = useSelector((state) => state);
  const filter = useSelector((state) => state.filter);

  // "Has the user moved the date picker?" — derived during render, not held in state, so the
  // answer is already correct on the very render that the change arrives. Holding it in state
  // would flip it one render LATE: the fetch effect below keys off the filter, which changes
  // in the same pass, so it would refetch once with the stale answer and not again.
  // The mount value is the baseline; anything different is a deliberate choice by the user.
  const dateKey = `${filter?.dateRange?.startDate || ''}|${filter?.dateRange?.endDate || ''}`;
  const initialDateKey = useRef(undefined);
  if (initialDateKey.current === undefined) initialDateKey.current = dateKey;
  const dateApplied = !lifetimeOnLoad || dateKey !== initialDateKey.current;

  // Sent to the API. Nulling dateRange makes buildQueryParams drop startDate/endDate entirely,
  // and the server skips its whole window stage when no range arrives — so this really is the
  // lifetime list, not a very wide window.
  const effectiveFilter = useMemo(
    () => (dateApplied ? filter : { ...filter, dateRange: null }),
    [filter, dateApplied],
  );

  const filterWithoutUTM = useMemo(() => {
    const { utm_options, ...rest } = effectiveFilter;
    setCurrentPage(1)
    return rest;
  }, [JSON.stringify(effectiveFilter)]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchSubscriptionUsers({
        filter: effectiveFilter, setUserData,
        page: currentPage,
        limit: pageLimit,
        skip: 0,
        searchTerm: filterWithoutUTM?.searchTerm
      });
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error while loading subscription users data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(filterWithoutUTM), currentPage]);

  const filteredData = userData

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleOpenProfile = (user) => {
    setSelectedUser(user);
    setOpenProfile(true);
  };

  const handleCloseProfile = () => setOpenProfile(false);

  const columnDefs = useMemo(
    () => {
      const defs = buildColumnDefs({ handleOpenTransactions, handleOpenProfile });
      // Hide the revenue "Total Transactions" + "Total Amount" columns on the
      // revenue-free Subscription page.
      const REVENUE_COLS = ['totalTransactionCount', 'totalTransactionAmount'];
      return hideRevenue
        ? defs.filter((c) => !REVENUE_COLS.includes(c.key))
        : defs;
    },
    [hideRevenue]
  );

  const handleSearch = () => {
    fetchData()
  }

  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return

    clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      dispatch(setSearchTermAction({ searchTerm }))
    }, 300);

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (searchTermFromStore == null && searchTerm) setSearchTerm(null);
  }, [searchTermFromStore])

  const handleSearchChange = (e) => {
    const value = e.target.value ?? '';
    setSearchTerm(value);
  };

  const handleExportExcel = () => {
    if (!userData?.length) return;
    const csvEscape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      'Subscription Status',
      'Subscriber Type',
      'Registration Date',
      'Subscription Date',
      'Next Renewal Date',
      'Cancelled On',
      ...(hideRevenue ? [] : ['Total Transactions', 'Total Amount']),
      'Currency',
      'Email',
      'Age',
      'Gender',
      'Plan',
      'Location',
      'Username',
      'Account Status',
    ];
    const rows = userData.map((u) => [
      u.subscriptionStatus ?? '',
      u.subscriberType ?? '',
      u.registrationDate ? formatDate(u.registrationDate) : '',
      u.startDate ? formatDate(u.startDate) : '',
      u.subscriptionStatus === 'Cancelled'
        ? ''
        : u.nextRenewalDate
          ? formatDate(u.nextRenewalDate)
          : '',
      u.cancelledDate ? formatDate(u.cancelledDate) : '',
      ...(hideRevenue
        ? []
        : [u.totalTransactionCount ?? 0, (u.totalTransactionAmount ?? 0).toFixed(2)]),
      u.transactionCurrency ? String(u.transactionCurrency).toUpperCase() : '',
      u.email ?? '',
      u.age ?? '',
      u.gender ?? '',
      u.subscriptionType ?? '',
      [u.city, u.state, u.country].filter(Boolean).join(', '),
      u.firstName ?? u.userName ?? '',
      accountStatusLabel(u),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map(csvEscape).join(','))
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `subscribed-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="mb-16 d-flex justify-content-between align-items-center">
        <h6 className="text-lg mb-0">{filter?.statsTitle ?? "Subscribed User List"}</h6>
        <div className="d-flex align-items-center gap-2">
          <form className="navbar-search" onSubmit={e => e.preventDefault()}>
            <input
              type="text"
              name="search"
              placeholder="Search"
              value={searchTerm || ""}
              autoComplete="off"
              onChange={handleSearchChange}
            />
            <span className="icon">
              <iconify-icon icon="ion:search-outline" />
            </span>
          </form>
          <Tooltip title="Export to Excel" arrow placement="top">
            <span>
              <button
                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                type="button"
                onClick={handleExportExcel}
                disabled={!userData?.length}
              >
                <FileDownloadOutlinedIcon />
              </button>
            </span>
          </Tooltip>
          <div className="dropdown">
            <Tooltip title="Manage Columns" arrow placement="top">
              <button
                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-auto-close="outside"
              >
                <ViewColumnOutlinedIcon />
              </button>
            </Tooltip>
            <ul
              className="dropdown-menu manage-list"
              onClick={(e) => e.stopPropagation()}
            >
              {columnDefs.map((column) => (
                <li key={column.key}>
                  <label
                    className="dropdown-item d-flex align-items-center gap-2"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      className="form-check-input m-0"
                      style={{ width: '18px', height: '18px' }}
                      type="checkbox"
                      checked={!!visibleColumns[column.key]}
                      onChange={() => handleColumnToggle(column.key)}
                    />
                    {column.label}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="table-scroll-container">
        <div
          ref={tableContainerRef}
          className="custom-horizontal-scroll scroll-sm"
          onScroll={handleTableScroll}
          style={{
            maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
            overflow: 'auto',
            position: 'relative'
          }}
        >
          <table className="table bordered-table sm-table mb-0" style={{ position: 'relative', }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
              <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                <th>#</th>
                {columnDefs
                  .filter((c) => visibleColumns[c.key])
                  .map((c) => (
                    <th key={c.key} className="text-center">
                      {c.label}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <UserTableSkeleton
                  rows={10}
                  columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 1}
                />
              ) : filteredData.length > 0 ? (
                filteredData.map((user, index) => (
                  <tr key={index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    {columnDefs
                      .filter((c) => visibleColumns[c.key])
                      .map((c) => (
                        <td
                          key={c.key}
                          className={
                            c.cellClass ? c.cellClass(user) : 'text-center'
                          }
                        >
                          {c.render(user)}
                        </td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="20" className="text-center py-3">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Sticky Scroll Bar */}
        {scrollState.scrollWidth > scrollState.clientWidth && (
          <div className="sticky-scroll-container">
            <div
              className="sticky-scroll-track"
              onClick={handleStickyScroll}
            >
              <div
                ref={scrollThumbRef}
                className="sticky-scroll-thumb"
                style={{
                  width: `${thumbWidth}px`,
                  transform: `translateX(${thumbPosition}px)`
                }}
                onMouseDown={handleThumbDrag}
              />
            </div>
          </div>
        )}
      </div>
      {/* Pagination */}
      {!loading && Boolean(totalPages) && <div className="d-flex justify-content-center mt-16">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          sx={{
            "& .MuiPaginationItem-root": {
              color: "#000",
              border: "1px solid #ddd",
            },

            "& .Mui-selected": {
              backgroundColor: "#ff4a4a !important",
              color: "#fff",
            },

            "& .MuiPaginationItem-root:hover": {
              backgroundColor: "#ffe5e5",
            },
          }}
        />
      </div>}

      <Modal
        open={openProfile}
        onClose={handleCloseProfile}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style2}>
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              backgroundColor: 'white',
              zIndex: 10,
              padding: '10px',
              borderBottom: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              sx={{
                position: 'absolute',
                top: 10,
                right: 0,
              }}
              onClick={handleCloseProfile}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <ProfileCard userId={selectedUser?.userId} />
        </Box>
      </Modal>

      <Modal
        open={Boolean(transactionsModalRow)}
        onClose={handleCloseTransactions}
        aria-labelledby="transactions-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 640 },
            maxHeight: '80vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <h6 className="text-lg mb-0" id="transactions-modal-title">
              Transactions —{' '}
              {transactionsModalRow?.firstName ??
                transactionsModalRow?.userName ??
                transactionsModalRow?.email ??
                ''}
            </h6>
            <IconButton onClick={handleCloseTransactions} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {transactionsModalRow?.transactions?.length ? (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                  <th>#</th>
                  <th>Date</th>
                  <th className="text-end">Amount</th>
                  <th className="text-center">Currency</th>
                </tr>
              </thead>
              <tbody>
                {transactionsModalRow.transactions.map((t, i) => (
                  <tr key={t._id ?? i}>
                    <td>{i + 1}</td>
                    <td>{t.createdAt ? formatDate(t.createdAt) : '-'}</td>
                    <td className="text-end">
                      {(t.amount ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="text-center">
                      {t.currency ? String(t.currency).toUpperCase() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} className="fw-semibold text-end">
                    Total
                  </td>
                  <td className="text-end fw-semibold">
                    {(
                      transactionsModalRow?.totalTransactionAmount ?? 0
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="text-center fw-semibold">
                    {transactionsModalRow?.transactionCurrency
                      ? String(
                        transactionsModalRow.transactionCurrency
                      ).toUpperCase()
                      : ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3, color: '#888' }}>
              No transactions found
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};
export default SubscribedUserList;
